import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { mutesService, type MutedEntity, type MutedEntityType } from '../../services/mutes';
import type { RootState } from '../store';

type MutedEntityMap = Record<MutedEntityType, Record<string, MutedEntity>>;

type MutedEntitiesState = {
  byType: MutedEntityMap;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  pending: Record<string, boolean>;
  lastLoadedAt: string | null;
};

const MUTES_CACHE_TTL_MS = 1000 * 60 * 60; // 60 minutes

const emptyMap = (): MutedEntityMap => ({
  composer: {},
  work: {},
  artist: {},
  recording: {}
});

const initialState: MutedEntitiesState = {
  byType: emptyMap(),
  status: 'idle',
  error: null,
  pending: {},
  lastLoadedAt: null
};

const buildCacheKey = (entityType: MutedEntityType, entityId: string) =>
  `${entityType}:${entityId}`;

const MUTES_FAILED_RETRY_MS = 1000 * 60 * 5; // 5 minutes

const getStaleForAge = (lastLoadedAt: string | null, maxAgeMs: number): boolean => {
  if (!lastLoadedAt) return true;
  const parsed = Date.parse(lastLoadedAt);
  if (!Number.isFinite(parsed)) return true;
  return Date.now() - parsed > maxAgeMs;
};

const shouldRefetchMutedEntities = (slice: MutedEntitiesState): boolean => {
  if (slice.status === 'loading') return false;
  if (slice.status === 'idle') return true;
  const maxAge = slice.status === 'failed' ? MUTES_FAILED_RETRY_MS : MUTES_CACHE_TTL_MS;
  return getStaleForAge(slice.lastLoadedAt, maxAge);
};

export const fetchMutedEntities = createAsyncThunk<
  MutedEntity[],
  void,
  { state: RootState }
>('mutedEntities/fetchAll', async () => {
  return mutesService.listMutes();
}, {
  condition: (_, { getState }) => {
    const { mutedEntities } = getState();
    return shouldRefetchMutedEntities(mutedEntities);
  }
});

export const muteEntity = createAsyncThunk<
  { entityType: MutedEntityType; entityId: string; mutedAt: string; notes?: string | null },
  { entityType: MutedEntityType; entityId: string; source?: string; notes?: string | null },
  { state: RootState }
>('mutedEntities/mute', async ({ entityType, entityId, source, notes }) => {
  await mutesService.muteEntity({ entityType, entityId, source, notes });
  return {
    entityType,
    entityId,
    mutedAt: new Date().toISOString(),
    notes: notes ?? null
  };
});

export const unmuteEntity = createAsyncThunk<
  { entityType: MutedEntityType; entityId: string },
  { entityType: MutedEntityType; entityId: string; source?: string },
  { state: RootState }
>('mutedEntities/unmute', async ({ entityType, entityId, source }) => {
  await mutesService.unmuteEntity(entityType, entityId, { source });
  return { entityType, entityId };
});

const mutateStateWithList = (state: MutedEntitiesState, payload: MutedEntity[]) => {
  const next = emptyMap();
  for (const entry of payload) {
    if (!entry || !entry.entityId) continue;
    next[entry.entityType][entry.entityId] = entry;
  }
  state.byType = next;
  state.lastLoadedAt = new Date().toISOString();
};

const mutedEntitiesSlice = createSlice({
  name: 'mutedEntities',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchMutedEntities.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchMutedEntities.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.error = null;
        mutateStateWithList(state, action.payload);
      })
      .addCase(fetchMutedEntities.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? 'Failed to load muted entities.';
        state.lastLoadedAt = new Date().toISOString();
      })
      .addCase(muteEntity.pending, (state, action) => {
        const { entityType, entityId } = action.meta.arg;
        state.pending[buildCacheKey(entityType, entityId)] = true;
      })
      .addCase(muteEntity.fulfilled, (state, action) => {
        const { entityType, entityId, mutedAt, notes } = action.payload;
        const cacheKey = buildCacheKey(entityType, entityId);
        delete state.pending[cacheKey];
        state.byType[entityType][entityId] = {
          entityType,
          entityId,
          mutedAt,
          notes: notes ?? undefined
        };
        state.lastLoadedAt = new Date().toISOString();
      })
      .addCase(muteEntity.rejected, (state, action) => {
        const { entityType, entityId } = action.meta.arg;
        delete state.pending[buildCacheKey(entityType, entityId)];
        state.error = action.error?.message ?? 'Failed to mute entity.';
      })
      .addCase(unmuteEntity.pending, (state, action) => {
        const { entityType, entityId } = action.meta.arg;
        state.pending[buildCacheKey(entityType, entityId)] = true;
      })
      .addCase(unmuteEntity.fulfilled, (state, action) => {
        const { entityType, entityId } = action.payload;
        const cacheKey = buildCacheKey(entityType, entityId);
        delete state.pending[cacheKey];
        delete state.byType[entityType][entityId];
        state.lastLoadedAt = new Date().toISOString();
      })
      .addCase(unmuteEntity.rejected, (state, action) => {
        const { entityType, entityId } = action.meta.arg;
        delete state.pending[buildCacheKey(entityType, entityId)];
        state.error = action.error?.message ?? 'Failed to unmute entity.';
      });
  }
});

export const mutedEntitiesReducer = mutedEntitiesSlice.reducer;

const selectMutedEntitiesState = (state: RootState) => state.mutedEntities;

export const selectMutedEntitiesStatus = (state: RootState) =>
  selectMutedEntitiesState(state).status;

export const selectMutedEntitiesLastLoadedAt = (state: RootState) =>
  selectMutedEntitiesState(state).lastLoadedAt;

export const selectMutedEntity = (
  state: RootState,
  entityType: MutedEntityType,
  entityId: string
): MutedEntity | undefined =>
  selectMutedEntitiesState(state).byType[entityType]?.[entityId];

export const selectIsEntityMuted = (
  state: RootState,
  entityType: MutedEntityType,
  entityId: string
): boolean => Boolean(selectMutedEntity(state, entityType, entityId));

export const selectMutedEntities = (state: RootState): MutedEntity[] => {
  const slice = selectMutedEntitiesState(state);
  const results: MutedEntity[] = [];
  (Object.keys(slice.byType) as MutedEntityType[]).forEach((entityType) => {
    const entries = slice.byType[entityType];
    Object.values(entries).forEach((entry) => results.push(entry));
  });
  results.sort((left, right) => right.mutedAt.localeCompare(left.mutedAt));
  return results;
};

export const selectMutePending = (
  state: RootState,
  entityType: MutedEntityType,
  entityId: string
): boolean => {
  const slice = selectMutedEntitiesState(state);
  return Boolean(slice.pending[buildCacheKey(entityType, entityId)]);
};

export const selectMutedEntitiesShouldRefresh = (state: RootState): boolean => {
  const slice = selectMutedEntitiesState(state);
  return shouldRefetchMutedEntities(slice);
};

export const mutedEntitiesSelectors = {
  selectMutedEntitiesStatus,
  selectMutedEntitiesLastLoadedAt,
  selectMutedEntity,
  selectIsEntityMuted,
  selectMutedEntities,
  selectMutePending,
  selectMutedEntitiesShouldRefresh
};
